// Gedeelde types tussen server (API) en client (formulieren/wizard).
// Dit is het contract voor de prijsberekening en boekingsflow.

export type BuildingType =
  | "appartement"
  | "rijwoning"
  | "halfopen_woning"
  | "open_bebouwing"
  | "ander_gebouw";

export const BUILDING_TYPE_LABELS: Record<BuildingType, string> = {
  appartement: "Appartement",
  rijwoning: "Rijwoning",
  halfopen_woning: "Halfopen woning",
  open_bebouwing: "Open bebouwing",
  ander_gebouw: "Ander gebouw",
};

export type ServiceAddOn = "epc" | "elektrische_keuring";

export interface BuildingDetails {
  buildingType: BuildingType;
  floors: number; // aantal bouwlagen incl. gelijkvloers
  hasBasement: boolean;
  hasOutbuildings: boolean;
  surfaceM2: number;
  buildYear: number;
  units: number; // aantal wooneenheden (1 voor eengezinswoning)
  notes?: string;
}

export interface ServiceSelection {
  addOns: ServiceAddOn[]; // asbestattest is altijd inbegrepen
  extraSamples: number; // extra staalnames boven de inbegrepen 2
  urgent: boolean; // spoedafspraak
}

export interface PriceLineItem {
  id: string;
  label: string;
  amountExclVat: number; // in eurocent
  info?: string;
}

export interface PriceBreakdown {
  currency: "EUR";
  lineItems: PriceLineItem[];
  subtotalExclVatCents: number;
  vatRatePercent: number;
  vatCents: number;
  contributionCents: number; // bv. OVAM-bijdrage; positief = toeslag, negatief = korting
  contributionLabel?: string;
  totalInclVatCents: number;
  estimatedDeliveryDays: number;
  includedNotes: string[];
  requiresCustomQuote: boolean;
  customQuoteReason?: string;
  // Wettelijk geen asbestattest vereist (bv. bouwjaar 2001 of later). Als dit
  // true is, is totalInclVatCents altijd 0 en mag er niet doorgeboekt worden.
  attestNotRequired?: boolean;
  attestNotRequiredReason?: string;
}

export interface ContactAddress {
  firstName: string;
  lastName: string;
  email: string;
  emailConfirm?: string;
  phone: string;
  street: string;
  houseNumber: string;
  bus?: string;
  postalCode: string;
  city: string;
  billingDifferent?: boolean;
  billingAddress?: string;
  vatNumber?: string;
  notes?: string;
}

export interface BookingSlot {
  date: string; // YYYY-MM-DD
  time: string; // HH:mm
}

export interface BookingDraft {
  building: BuildingDetails;
  service: ServiceSelection;
  contact: ContactAddress;
  slot: BookingSlot | null;
  acceptedTerms: boolean;
  acceptedPrivacy: boolean;
  marketingConsent: boolean;
  honeypot?: string;
  formRenderedAt?: number;
}

export interface DaySlots {
  date: string;
  isOpen: boolean;
  closedReason?: string;
  slots: { time: string; period: "voormiddag" | "namiddag"; available: boolean }[];
}

export type OrgKind = "vme" | "bedrijf";

export interface QuoteBuildingInfo {
  address: string;
  postalCode: string;
  city: string;
  buildingType: string;
  buildYear?: number;
  floors?: number;
  entrances?: number;
  stairwells?: number;
  corridors?: number;
  privateUnits?: number;
  hasElevator: boolean;
  hasBasement: boolean;
  hasGarages: boolean;
  technicalRooms: boolean;
  surfaceM2?: number;
  inUse: boolean;
}

export interface QuoteRequestDraft {
  orgKind: OrgKind;
  orgName: string;
  vatNumber?: string;
  contactPerson: string;
  role?: string;
  email: string;
  phone: string;
  billingInfo?: string;
  preferredContact: "email" | "telefoon";
  building: QuoteBuildingInfo;
  services: string[];
  message?: string;
  honeypot?: string;
  formRenderedAt?: number;
}

export interface ApiError {
  ok: false;
  error: string;
  fieldErrors?: Record<string, string>;
}

export interface ApiOk<T> {
  ok: true;
  data: T;
}

export type ApiResult<T> = ApiOk<T> | ApiError;
